import { useEffect, useRef, useState } from 'react';
import { useSurveyI18n } from '../i18n';
import { fmtDate } from '../utils';
import { fmtCardNumber, roundLabel, STORE_MAX, validateApproval, type ApprovalErrorCode, type SurveyWinner } from '../winners';

/*
 * "Provide information and approve" (design board "Winners & prizes"): the
 * store and the display name for one weekly winner. Save stays disabled until
 * both are filled; the full rule is checked on save so the message can say
 * which field is wrong. Escape or Cancel closes it; a stray click on the
 * backdrop does not, so a half-typed address is never lost.
 */
export function ApproveDialog({
  winner,
  saving,
  serverError,
  onCancel,
  onSave,
}: {
  winner: SurveyWinner;
  saving: boolean;
  serverError: string | null;
  onCancel: () => void;
  onSave: (body: { storeAddress: string; displayName: string }) => void;
}) {
  const [store, setStore] = useState(winner.storeAddress ?? '');
  const [name, setName] = useState(winner.displayName ?? '');
  const { t } = useSurveyI18n();
  const [error, setError] = useState<{ field: 'store' | 'name'; code: ApprovalErrorCode } | null>(null);
  const storeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    storeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onCancel();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const ready = store.trim() !== '' && name.trim() !== '' && !saving;

  function save() {
    const result = validateApproval(store, name);
    if ('code' in result) {
      setError(result);
      return;
    }
    setError(null);
    onSave(result);
  }

  return (
    <div className="su-backdrop">
      <div className="su-dialog" role="dialog" aria-modal="true" aria-labelledby="su-approve-title">
        <div className="su-stack">
          <h3 id="su-approve-title">{t.provideAndApprove}</h3>
          <span className="su-note">{t.dialogMeta(roundLabel(winner, t), fmtDate(winner.drawnAt), fmtCardNumber(winner.cardNumber))}</span>
        </div>
        <div className="su-field">
          <label htmlFor="su-approve-store">{t.storeAddress}</label>
          <input
            id="su-approve-store"
            ref={storeRef}
            type="text"
            value={store}
            maxLength={STORE_MAX}
            onChange={(e) => setStore(e.target.value)}
            placeholder={t.storePlaceholder}
            aria-invalid={error?.field === 'store'}
          />
          {error?.field === 'store' && <span className="su-field-error">{t.approvalError[error.code]}</span>}
        </div>
        <div className="su-field">
          <label htmlFor="su-approve-name">{t.displayName}</label>
          <input
            id="su-approve-name"
            type="text"
            value={name}
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && ready && save()}
            placeholder={t.namePlaceholder}
            aria-invalid={error?.field === 'name'}
            aria-describedby="su-approve-name-help"
          />
          {error?.field === 'name' ? (
            <span id="su-approve-name-help" className="su-field-error">
              {t.approvalError[error.code]}
            </span>
          ) : (
            <span id="su-approve-name-help" className="su-field-hint">
              {t.nameHint}
            </span>
          )}
        </div>
        {/* Approving publishes both fields to every player, for good. */}
        <p className="su-warning">{t.approveWarning}</p>
        {serverError && <div className="su-error">{serverError}</div>}
        <div className="su-dialog-actions">
          <button type="button" className="su-secondary" onClick={onCancel}>
            {t.cancel}
          </button>
          <button type="button" className="su-primary" disabled={!ready} onClick={save}>
            {saving ? t.saving : t.saveAndApprove}
          </button>
        </div>
      </div>
    </div>
  );
}
